/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'
import abstraction_layersImg from 'assets/images/abstraction-layers.jpg'
import react_redux_flowImg from 'assets/images/react-redux-flow.png'

export const _1 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>1 - Селекторы</h2>
    <p className={styles.description}>
      Компоненты не должны знать о структуре состояния, их задача - создание разметки по полученным{' '}
      <code className={styles.code}>props</code>. В случае изменения структуры состояния, а это естественный процесс,
      все компоненты которые подписаны на его измененную часть придется рефакторить. В решении этой проблемы помогают
      селекторы.{' '}
    </p>
    <p className={styles.description}>
      <b>Селектор</b> — функция которая получает ссылку на все состояние и произвольное кол-во других аргументов.
      Возвращает только определенную часть состояния или вычисляемые данные основываясь на состоянии.{' '}
    </p>
    <p className={styles.description}>
      Наибольшая выгода получается при использовании композиции селекторов для рассчета производных данных (
      <code className={styles.code}>derived data</code>).{' '}
    </p>
    <p className={styles.description}>
      Допустим есть состояние хранящее информацию о постах и текущую сессию пользователя.{' '}
    </p>
    <CopyBlock
      text={`const reduxStateShape = {
  session: {
    user: {
      name: 'Mango',
      email: 'mango@gmail.com',
    },
    authenticated: true,
    error: null,
  },
  posts: {
    items: [],
    loading: false,
    selectedTags: [],
  },
};`}
      language="javascript"
      showLineNumbers={false}
      theme={ocean}
    />
    <p className={styles.description}>Тогда файл селекторов содержит следующие экспорты. </p>
    <CopyBlock
      text={`// posts-selectors.js
export const getAllPosts = state => state.posts.items;

export const isPostsLoading = state => state.posts.loading;

export const getPostById = (state, id) => {
  const posts = getAllPosts(state);

  return posts.find(post => post.id === id);
};

const getSelectedTags = state => state.posts.selectedTags;

export const getPostsWithTags = state => {
  const posts = getAllPosts(state);
  const tags = getSelectedTags(state);

  return posts.filter(post => tags.some(tag => post.tags.includes(tag)));
};

// session-selectors.js
export const getUserName = state => state.session.user.name;
export const isUserAuthenticated = state => state.session.authenticated;`}
      language="javascript"
      showLineNumbers={false}
      theme={ocean}
    />
    <p className={styles.description}>Контейнер всех постов подписывается на изменения. </p>
    <CopyBlock
      text={`// PostsContainer.js
import { connect } from 'react-redux';
import Posts from 'path/to/posts';
import {
  getAllPosts,
  getPostsWithTags,
  isPostsLoading,
} from 'path/to/selectors';

const mapStateToProps = state => ({
  posts: getAllPosts(state),
  postsWithTags: getPostsWithTags(state),
  isLoading: isPostsLoading(state),
});

export default connect(
  mapStateToProps,
  null,
)(Posts);`}
      language="javascript"
      showLineNumbers={false}
      theme={ocean}
    />
    <p className={styles.description}>Контейнер выбраного поста подписывается на изменения. </p>
    <CopyBlock
      text={`// SelectedPostContainer.js
import { connect } from 'react-redux';
import Post from 'path/to/post';
import { getPostById } from 'path/to/selectors';

const mapStateToProps = (state, props) => ({
  post: getPostById(state, props.id),
});

export default connect(
  mapStateToProps,
  null,
)(Posts);`}
      language="javascript"
      showLineNumbers={false}
      theme={ocean}
    />
    <p className={styles.description}>
      Таким образом, при изменении структуры состояния, рефакторить необходимо будет только селекторы, а не все
      компоненты.{' '}
    </p>
    <p className={styles.description}>
      Селекторы могут быть сложными и выполнять вычисления, это помогает хранить минимально необходимую информацию в{' '}
      <code className={styles.code}>store</code>, не захламляя его вычисляемыми данными.
    </p>
    <p className={styles.description}>
      Использование селекторов не ограничего функцией <code className={styles.code}>mapStateToProps</code>, их можно
      применять в действиях или в прослойках.{' '}
    </p>
    <img className={styles.img} src={abstraction_layersImg} alt="abstraction-layers" />
    <p className={styles.description}>Полная архитектура React\Redux приложения. </p>
    <img className={styles.img} src={react_redux_flowImg} alt="react-redux-flow" />
    <a
      className={styles.link}
      href="https://medium.com/mofed/react-redux-architecture-overview-7b3e52004b6e"
      target="_blank"
    >
      React + Redux: Architecture Overview
    </a>{' '}
  </section>
))
